import { VerificationStep } from "../common/constant";

// Resolve a possibly-populated tierSetting (string id, or populated { _id }) to its id.
export const tierIdOf = (h) => h?.tierSetting?._id || h?.tierSetting;

const sortByNewest = (histories) =>
    [...(histories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

/**
 * Newest history record per tier level, plus the failed→blocked relationship:
 * when a lower tier is `verification_failed` and a higher tier is still
 * `draft_verification`, that higher draft is "blocked" and must be cancelled
 * before the failed lower tier can be redone. Shared by all 3 layouts so this
 * logic cannot drift.
 */
export const resolveBlockedTier = (histories = []) => {
    const sorted = sortByNewest(histories);
    const latestByTierLevel = sorted.reduce((acc, h) => {
        if (h && !acc[h.tierLevel]) acc[h.tierLevel] = h;
        return acc;
    }, {});
    const levelHistories = Object.values(latestByTierLevel);
    const failedTierHistory =
        [...levelHistories].sort((a, b) => a.tierLevel - b.tierLevel).find((h) => h.status === "verification_failed") || null;
    const blockedTierHistory = failedTierHistory
        ? levelHistories.find((h) => h.tierLevel > failedTierHistory.tierLevel && h.status === "draft_verification") || null
        : null;
    return {
        blockedByFailedTier: !!(failedTierHistory && blockedTierHistory),
        failedTierHistory,
        blockedTierHistory,
        latestByTierLevel,
    };
};

/**
 * Decide which verification step to land on, derived purely from
 * profile + tiers + histories. Returns { step } and, when resuming a draft,
 * { selectedTierId } for the caller to apply via setSelectedTier.
 */
export const resolveInitialStep = ({ profile, tiers = [], histories = [] }) => {
    const sorted = sortByNewest(histories);
    const latest = sorted[0];

    const levels = (tiers || []).map((tier) => tier?.tierLevel).filter(Number.isFinite);
    const maxLevel = levels.length ? Math.max(...levels) : 0;
    const nextTierAvailableStatus = ["pending_verification", "verified"].includes(latest?.status);
    const tierSettingNextTierExist = Number.isFinite(latest?.tierLevel) && latest.tierLevel < maxLevel;

    const { blockedByFailedTier, latestByTierLevel } = resolveBlockedTier(histories);
    const activeDraft = Object.values(latestByTierLevel).find((h) => h.status === "draft_verification");
    const hasPending = sorted.some((h) => h?.status === "pending_verification");

    if (nextTierAvailableStatus && tierSettingNextTierExist) return { step: VerificationStep.Tier };
    if (blockedByFailedTier) return { step: VerificationStep.Tier };
    if (activeDraft) return { step: VerificationStep.Document, selectedTierId: tierIdOf(activeDraft) };
    if (hasPending) return { step: VerificationStep.Status };
    if (profile?.flowSetting) return { step: VerificationStep.Tier };
    if (profile?.status === "not_verified") return { step: VerificationStep.Country };
    return { step: VerificationStep.Flow };
};
